import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import {
  GRID_SURFACE_BASE_VAR,
  GRID_SURFACE_TINT_VAR,
  getGridScrollFadeShadow,
  getTintedSurface,
} from '../../../../../../styles/gridScrollFade';

interface Params {
  canScrollRight?: boolean;
  canScrollLeft?: boolean;
  highlightProduct: boolean;
  hasExtraButton: boolean;
  hasTouchGrid: boolean;
}

type GetGridArrowHeightParams = Pick<Params, 'hasExtraButton' | 'hasTouchGrid'>;
export const GRID_ARROW_WIDTH = 12;

const TOUCH_GRID_SPACING_IN_PIXELS = 4;
const DESKTOP_GRID_SPACING_IN_PIXELS = 2;

export const getGridSpacing = (hasTouchGrid: boolean) =>
  hasTouchGrid ? TOUCH_GRID_SPACING_IN_PIXELS : DESKTOP_GRID_SPACING_IN_PIXELS;

const GRID_ARROW_HEIGHT_WITHOUT_EXTRA_BUTTON = 44;
const GRID_ARROW_HEIGHT_WITH_EXTRA_BUTTON = 30;
const TOUCH_GRID_ARROW_HEIGHT_WITH_EXTRA_BUTTON = 80;

export const getGridSizeLabelHeight = (theme: Theme) =>
  Math.round(
    Number.parseFloat(String(theme.typography.body2.fontSize)) *
      Number(theme.typography.body2.lineHeight),
  );

const getGridArrowHeight = ({ hasExtraButton, hasTouchGrid }: GetGridArrowHeightParams) => {
  if (!hasExtraButton) {
    return GRID_ARROW_HEIGHT_WITHOUT_EXTRA_BUTTON;
  }

  return hasTouchGrid
    ? TOUCH_GRID_ARROW_HEIGHT_WITH_EXTRA_BUTTON
    : GRID_ARROW_HEIGHT_WITH_EXTRA_BUTTON;
};

export const useHorizontalScrollStyles = makeStyles<Params>()(
  (theme, { hasExtraButton, hasTouchGrid, highlightProduct, canScrollLeft, canScrollRight }) => {
    const fallbackSurface = highlightProduct
      ? theme.palette.background.default
      : theme.palette.background.paper;
    const surfaceParams = {
      base: `var(${GRID_SURFACE_BASE_VAR}, ${fallbackSurface})`,
      tint: `var(${GRID_SURFACE_TINT_VAR}, transparent)`,
    };
    const surface = getTintedSurface(surfaceParams);
    const surfaceShadow = getGridScrollFadeShadow(surfaceParams);

    const sizeLabelOffset = hasExtraButton ? getGridSizeLabelHeight(theme) : 0;

    return {
      arrowContainerLeft: {
        left: 0,
        width: 0,
        display: 'flex',
        alignItems: 'flex-end',
        paddingTop: sizeLabelOffset,
      },
      actionsContainerRight: {
        right: 0,
        display: 'flex',
        alignItems: hasExtraButton ? 'center' : 'flex-end',
        paddingTop: sizeLabelOffset,
      },
      stickyContent: {
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
      },
      scrollContainerRight: {
        ...surface,
        boxShadow: canScrollRight ? surfaceShadow : 'none',
        '&:hover': {
          cursor: canScrollRight ? 'pointer' : 'auto',
        },
      },
      scrollContainerLeft: {
        ...surface,
        boxShadow: canScrollLeft ? surfaceShadow : 'none',
        '&:hover': {
          cursor: canScrollLeft ? 'pointer' : 'auto',
        },
      },
      arrow: {
        width: GRID_ARROW_WIDTH,
        height: getGridArrowHeight({ hasExtraButton, hasTouchGrid }),
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
          borderRadius: theme.shape.borderRadiusSm,
        },
      },
    };
  },
);
